import { z } from 'zod';

import { CreateSetDateWindowSchema } from '../filters/date-window.js';

export const SET_NAME_MIN = 3;
export const SET_NAME_MAX = 100;

/**
 * Matches only ASCII-safe characters: letters, digits, spaces,
 * and common punctuation (hyphens, underscores, periods, commas,
 * ampersands, parentheses, apostrophes).
 */
const ENGLISH_SAFE_PATTERN = /^[a-zA-Z0-9 \-_.,&()']+$/;

export const SetNameSchema = z
  .string()
  .trim()
  .min(SET_NAME_MIN, `Name must be at least ${SET_NAME_MIN} characters`)
  .max(SET_NAME_MAX, `Name must be ${SET_NAME_MAX} characters or fewer`)
  .regex(
    ENGLISH_SAFE_PATTERN,
    'Name can only contain English letters, numbers, spaces, and basic punctuation (- _ . , & () \')',
  );

/**
 * Default name the web create-set dialog and the MCP save tools share.
 * UTC so a server and a browser agree on the same instant.
 */
export function createDefaultPersonaSetName(referenceDate = new Date()): string {
  const year = String(referenceDate.getUTCFullYear()).slice(-2);
  const month = String(referenceDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(referenceDate.getUTCDate()).padStart(2, '0');
  const hours = String(referenceDate.getUTCHours()).padStart(2, '0');
  const minutes = String(referenceDate.getUTCMinutes()).padStart(2, '0');
  return `Set ${year}${month}${day}-${hours}${minutes}`;
}

/** `YYMMDD-HHMM` in `timeZone`, matching the hub's default set stamp. Invalid zones fall back to UTC. */
export function formatHubSetStamp(referenceDate: Date, timeZone = 'UTC'): string {
  let zone = timeZone.trim() || 'UTC';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone }).format();
  } catch {
    zone = 'UTC';
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(referenceDate);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '00';
  return `${pick('year')}${pick('month')}${pick('day')}-${pick('hour')}${pick('minute')}`;
}

/** Hub default: the set kind plus the current date and time, e.g. `Post-Flight 261005-1504`. */
export function createNamedHubSetName(
  name: string,
  referenceDate = new Date(),
  timeZone = 'UTC',
): string {
  return `${name} ${formatHubSetStamp(referenceDate, timeZone)}`;
}

export const IndustrySchema = z.string().trim().min(1, 'Industry is required');

export const CreateSetBodySchema = z
  .object({
    name: SetNameSchema.optional(),
    industry: IndustrySchema,
    myBrand: z.string().max(255).nullish(),
    mainCompetitor: z.string().max(255).nullish(),
    industryCompetitors: z.array(z.string().trim().min(1)).optional(),
    products: z.array(z.string().trim().min(1)).optional(),
    adPlatforms: z.array(z.string().trim().min(1)).optional(),
    strategicPriorities: z.object({
      focusOn: z.array(z.string().trim().min(1)).optional(),
      avoid: z.array(z.string().trim().min(1)).optional(),
    }).optional(),
    brandVoice: z.object({
      alwaysSay: z.string().trim().optional(),
      neverSay: z.string().trim().optional(),
    }).optional(),
    scriptGuidelines: z.string().trim().optional(),
  })
  .and(CreateSetDateWindowSchema);

export type CreateSetBody = z.infer<typeof CreateSetBodySchema>;
